<script lang="ts">
    import { getTime } from "date-fns";
    import { onDestroy, onMount, type Snippet } from "svelte";
    import * as o9n from "o9n";
    import Swal from "sweetalert2";
    import type { ElearningFile } from "../elearning.js";
    import { createBlobUrl, requestFullScreen, videoToImage } from "../elearning.js";
    import { VideoEvento, VideoStream } from "./index.js";

    interface Props {
        timeout?: number;
        file: ElearningFile;
        type?: string;
        ended?: boolean;
        progress?: number;
        fullscreen?: boolean;
        enableProgress?: boolean;
        frameTimeInSeconds?: number;
        children?: Snippet | null;
    }

    let {
        timeout = 250,
        file,
        type,
        ended = $bindable(false),
        progress = 0,
        fullscreen = false,
        enableProgress = true,
        frameTimeInSeconds = 0.25,
        children = null,
    }: Props = $props();

    let wrapper: HTMLElement | null = $state(null);
    let videoInstance: ReturnType<typeof VideoEvento | typeof VideoStream> | null = $state(null);
    const video = $derived(videoInstance?.getElement());
    let landscape: boolean = $state(isLandscape());

    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
            toast.onmouseenter = Swal.stopTimer;
            toast.onmouseleave = Swal.resumeTimer;
        },
    });

    onMount(function () {
        screen?.orientation?.addEventListener("change", waitToLandscape, true);
    });

    onDestroy(function () {
        screen?.orientation?.removeEventListener("change", waitToLandscape, true);
    });

    function isLandscape(event: any = undefined): boolean {
        if (event?.targer?.type) {
            return event.target.type.includes("landscape");
        }

        if (screen?.orientation?.type) {
            return screen.orientation.type.includes("landscape");
        }

        return Math.abs(window.orientation) === 90;
    }

    function isMobile(): boolean {
        return window.navigator.maxTouchPoints > 1 || "ontouchstart" in document;
    }

    function waitToLandscape(event: any): void {
        landscape = isLandscape(event);
        if (!landscape && isPlaying(video)) {
            exitFullScreen(wrapper!);
            restart(video);
            o9n.orientation?.unlock();

            Toast.fire({
                icon: "warning",
                title: "Video přerušeno!",
                text: "Během přehrávání neměňte orientaci zařízení",
            });
        }
    }

    function exitFullScreen(element: Document | HTMLElement = window.document): Promise<void> | void {
        if (!(element instanceof Document)) {
            element = element.ownerDocument;
        }

        if (element.exitFullscreen) {
            return element.exitFullscreen();
        } else if (element.webkitExitFullscreen) {
            return element.webkitExitFullscreen();
        } else if (element.mozCancelFullScreen) {
            return element.mozCancelFullScreen();
        } else if (element.msExitFullscreen) {
            return element.msExitFullscreen();
        }

        throw new Error("Nelze opustit fullscreen!");
    }

    function isPlaying(video: HTMLVideoElement): boolean {
        return !video.paused && !video.ended && video.currentTime > 0;
    }

    async function start(): Promise<void> {
        restart(video);
        await requestFullScreen(wrapper!);

        if (isMobile() && !landscape) {
            if (o9n.orientation?.lock instanceof Function) {
                await o9n.orientation?.lock("landscape")?.catch(() => {
                    //
                });
            }

            await sleep(timeout);

            if (!landscape) {
                landscape = isLandscape();
            }

            if (!landscape) {
                errorLandscape();
                return;
            }
        }

        await video.play();
    }

    async function sleep(ms: number): Promise<void> {
        await new Promise((r) => setTimeout(r, ms));
    }

    function errorLandscape(): void {
        o9n.orientation?.unlock();

        exitFullScreen(wrapper!);
        restart(video);

        console.error("Nelze vynutit režim na šířku!");
        Toast.fire({
            icon: "warning",
            title: "Otočte zařízení na šířku a zkuste znovu!",
        });
    }

    function restart(video: HTMLVideoElement): void {
        video.pause();
        video.currentTime = 0;
    }

    function onEnd(): void {
        ended = true;
        exitFullScreen(wrapper!);
    }

    function onFullScreen(): void {
        fullscreen = document.fullscreenElement !== null;
        if (!fullscreen && video) {
            restart(video);
            o9n.orientation?.unlock();
        }
    }

    function onTime(): void {
        progress = isNaN(video.duration) ? 0 : Math.round((video.currentTime / video.duration) * 100);
    }

    async function prepareMedia(file: ElearningFile) {
        if (file.is_remote) {
            return file.source;
        }

        return createBlobUrl(file.source);
    }

    async function prepareThumbnail(file: ElearningFile, frameTimeInSeconds: number) {
        if (file?.thumbnail) {
            let target = file?.thumbnail?.source;
            if (file?.updated_at) {
                target += `?${getTime(file.updated_at)}`;
            }

            return createBlobUrl(target);
        }

        return videoToImage(file.source, frameTimeInSeconds);
    }

    // TODO: debug podle proměnné DEV a VITE_DEBUG / popřípadě propu
</script>

<svelte:body onfullscreenchange={onFullScreen} />


    <div class="relative grid h-80 w-full place-items-center bg-black/10">
        {#await prepareMedia(file)}
            <p class="text-center text-white">Video se stahuje...</p>
        {:then src}
            <div bind:this={wrapper} class="video-container pointer-events-none" class:hidden={!fullscreen}>
                {#if file.is_remote}
                    <VideoStream bind:this={videoInstance} onended={onEnd} ontimeupdate={onTime} source={src} />
                {:else}
                    <VideoEvento bind:this={videoInstance} onended={onEnd} ontimeupdate={onTime}>
                        <source {src} {type} />
                    </VideoEvento>
                {/if}

                {#if fullscreen && enableProgress}
                    <progress
                        max={100}
                        value={progress}
                        class="absolute right-0 bottom-0 left-0 z-50 h-2 w-full text-center"
                    >
                        {video.currentTime}/{isNaN(video.duration) ? 0 : video.duration}
                    </progress>
                {/if}
            </div>

            <button class="bg-primary cursor-pointer px-4 py-2 font-bold text-white" onclick={start} type="button">
                Start
            </button>
        {/await}

        {#if children}
            {@render children()}
        {:else}
            {#await prepareThumbnail(file, frameTimeInSeconds)}
                <div class="bg-primary absolute top-0 right-0 bottom-0 left-0 -z-10 h-full w-full p-2 text-white">
                    Náhled se načítá...
                </div>
            {:then image}
                <img
                    src={image}
                    class="absolute top-0 right-0 bottom-0 left-0 -z-10 h-full w-full object-cover object-center"
                    alt="Náhled videa"
                />
            {:catch exception}
                {(console.error(exception), "")}

                <div class="bg-primary absolute top-0 right-0 bottom-0 left-0 -z-10 h-full w-full p-2 text-white">
                    Náhled nelze načíst
                </div>
            {/await}
        {/if}
    </div>


<style>
    @reference "tailwindcss/theme";

    .video-container {
        @apply relative bg-black;

        :global(& video) {
            @apply absolute top-0 right-0 bottom-2 left-0 z-40 mx-auto h-full w-full max-w-full;
            max-height: calc(100% - 0.5rem);
        }

        :global(& video::-webkit-media-controls-panel) {
            display: none !important;
            opacity: 1 !important;
        }
    }
</style>
